import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  Users,
  Phone,
  Mail,
  User,
  Tablet,
  FileText,
  Hash,
} from "lucide-react";
import {
  formatFullDate,
} from "@/components/tickets/config";
import { formatActivityDateTime } from "@/components/bookings/formatActivityDateTime";
import { BookingStatusBadge } from "./BookingStatusBadge";
import { BookingCancellationSource } from "./BookingCancellationSource";
import { ClickToCopyId } from "@/components/common";
import { DefinitionList, DetailRow } from "@/components/ui";
import type { BookingListRow } from "./bookingViewModel";
import { bookingPartyCount } from "./bookingPartyCount";
import type { BookingViewer } from "./BookingCard";
import type { Booking } from "@/types";

import { formatMoney } from "@/lib/money";
import { getUpfrontPaidCents } from "./sellerBookingRules";

function resolveVisitTimeDisplay(
  row: BookingListRow,
  localTimeSuffix: (time: string) => string,
): string | null {
  const slot = row.timeSlot?.trim();
  if (row.visitAt) {
    const labels = formatActivityDateTime(
      row.visitAt,
      row.activityRegionCode,
      localTimeSuffix,
    );
    if (labels) {
      return labels.timeLabel;
    }
  }
  return slot || null;
}

interface BookingDetailInfoProps {
  row: BookingListRow;
  booking: Booking;
  /** Only `"customer"` vs not matters here; partner/staff both show guest contact. */
  viewer: BookingViewer;
}

export function BookingDetailInfo({
  row,
  booking,
  viewer,
}: BookingDetailInfoProps) {
  const isCustomer = viewer === "customer";
  const { t } = useTranslation("bookings");
  const localTimeSuffix = (time: string) => t("adminDetail.localTime", { time });
  const visitTime = resolveVisitTimeDisplay(row, localTimeSuffix);
  const visitDateLabel = row.visitAt
    ? formatActivityDateTime(
        row.visitAt,
        row.activityRegionCode,
        localTimeSuffix,
      )?.dateLabel ?? null
    : null;
  const partyCount = bookingPartyCount(row);
  const partyLabel = t("guest", { count: partyCount });

  const cancellationSource =
    row.status === "CANCELED" ? row.cancellationSource : null;
  const paidCents = getUpfrontPaidCents(row);

  return (
    <DefinitionList className="divide-y divide-surface-100">
      <div className="pb-4">
        <BookingStatusBadge status={row.status} handshakeStatus={row.handshakeStatus} />
      </div>

      {cancellationSource ? (
        <div className="pb-4">
          <BookingCancellationSource
            source={cancellationSource}
            variant="detail"
          />
        </div>
      ) : null}

      <DetailRow icon={Hash} label={t("detail.bookingId")}>
        <ClickToCopyId
          value={row.id}
          className="break-all text-xs text-surface-900"
        />
      </DetailRow>

      {row.serviceName ? (
        <DetailRow icon={FileText} label={t("detail.services")}>
          {row.serviceName}
        </DetailRow>
      ) : null}

      <DetailRow icon={Calendar} label={t("detail.visitDate")}>
        {visitDateLabel}
      </DetailRow>

      <DetailRow icon={Clock} label={t("detail.visitTime")}>
        {visitTime ?? "—"}
      </DetailRow>

      <DetailRow icon={Users} label={t("detail.partySize")}>
        {partyLabel}
      </DetailRow>

      {!isCustomer && row.customerDisplayName ? (
        <DetailRow icon={User} label={t("detail.customerName")}>
          {row.customerDisplayName}
        </DetailRow>
      ) : null}

      {!isCustomer && row.contactInfo.email ? (
        <DetailRow icon={Mail} label={t("detail.email")}>
          {row.contactInfo.email}
        </DetailRow>
      ) : null}

      {!isCustomer && row.contactInfo.phone ? (
        <DetailRow icon={Phone} label={t("detail.phone")}>
          {row.contactInfo.phone}
        </DetailRow>
      ) : null}

      {row.kioskName ? (
        <DetailRow icon={Tablet} label={t("detail.kiosk")}>
          <span>{row.kioskName}</span>
        </DetailRow>
      ) : null}

      {paidCents != null ? (
        <DetailRow
          icon={FileText}
          label={
            isCustomer
              ? t("detail.total")
              : row.isDeposit
                ? t("detail.depositPaid")
                : t("detail.amountPaid")
          }
        >
          {formatMoney(paidCents)}
        </DetailRow>
      ) : null}

      {!isCustomer &&
      row.isDeposit &&
      row.balanceDueAtVenueCents != null ? (
        <DetailRow icon={FileText} label={t("detail.dueAtVenue")}>
          {formatMoney(row.balanceDueAtVenueCents)}
        </DetailRow>
      ) : null}

      <DetailRow icon={Calendar} label={t("detail.createdAt")}>
        {formatFullDate(row.createdAt)}
      </DetailRow>

      {booking.customer_notes ? (
        <DetailRow icon={FileText} label={t("detail.customerNotes")}>
          <p className="whitespace-pre-wrap">{booking.customer_notes}</p>
        </DetailRow>
      ) : null}

      {booking.seller_notes ? (
        <DetailRow icon={FileText} label={t("detail.sellerNotes")}>
          <p className="whitespace-pre-wrap">{booking.seller_notes}</p>
        </DetailRow>
      ) : null}
    </DefinitionList>
  );
}
